import { Container, Box, Typography, Paper, Breadcrumbs, Link, Button } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import Hero from '../components/common/Hero';
import HomeIcon from '@mui/icons-material/Home';
import PolicyIcon from '@mui/icons-material/Policy';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <>
      <Helmet>
        <title>Privacy Policy | Brynleigh Estates HOA</title>
        <meta name="description" content="Privacy Policy for the Brynleigh Estates HOA website. Learn how we collect, use, and protect your personal information." />
        <link rel="canonical" href="https://brynleighestates.com/privacy-policy" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://brynleighestates.com/privacy-policy" />
        <meta property="og:title" content="Privacy Policy | Brynleigh Estates HOA" />
        <meta property="og:description" content="Privacy Policy for the Brynleigh Estates HOA website. Learn how we collect, use, and protect your information." />
        <meta property="og:image" content="https://brynleighestates.com/images/brynleigh-estates-social-logo.jpeg" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Privacy Policy | Brynleigh Estates HOA" />
        <meta name="twitter:description" content="Privacy Policy for the Brynleigh Estates HOA website." />
      </Helmet>
      
      <Hero 
        imageUrl="/images/hills-bg.jpeg"
        title="Privacy Policy"
        subtitle="How we collect, use, and protect your information"
        height="small"
      />
      
      <Container maxWidth="lg">
        <Box sx={{ my: 4 }}>
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
            <Link 
              underline="hover" 
              color="inherit" 
              component={RouterLink}
              to="/" 
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              Home
            </Link>
            <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
              <PolicyIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              Privacy Policy
            </Typography>
          </Breadcrumbs>
          
          <Button 
            variant="outlined" 
            startIcon={<ArrowBackIcon />} 
            onClick={() => navigate(-1)}
            sx={{ mb: 4 }}
          >
            Go Back
          </Button>

          <Paper elevation={0} sx={{ p: 4, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Typography variant="h4" component="h1" gutterBottom>
              Privacy Policy
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
              Last updated: January 2026
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
              Introduction
            </Typography>
            <Typography variant="body1" paragraph>
              Brynleigh Estates Homeowners Association ("we," "our," or "us") respects your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, and safeguard information when you visit our website or interact with our services.
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
              Information We Collect
            </Typography>
            <Typography variant="body1" paragraph>
              We may collect the following types of information:
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 2 }}>
              <Box component="li" sx={{ mb: 1 }}>
                <Typography variant="body1">
                  <strong>Contact Information:</strong> Name, email address, phone number, and mailing address when you submit forms or contact us.
                </Typography>
              </Box>
              <Box component="li" sx={{ mb: 1 }}>
                <Typography variant="body1">
                  <strong>Property Information:</strong> Address and lot number within Brynleigh Estates.
                </Typography>
              </Box>
              <Box component="li" sx={{ mb: 1 }}>
                <Typography variant="body1">
                  <strong>Communication Data:</strong> Messages, requests, and correspondence you send to the HOA board.
                </Typography>
              </Box>
              <Box component="li" sx={{ mb: 1 }}>
                <Typography variant="body1">
                  <strong>Technical Data:</strong> Browser type, IP address, and usage patterns collected automatically when you visit our website.
                </Typography>
              </Box>
            </Box>

            <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
              How We Use Your Information
            </Typography>
            <Typography variant="body1" paragraph>
              We use the information we collect to:
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 2 }}>
              <Box component="li" sx={{ mb: 1 }}>
                <Typography variant="body1">Respond to your inquiries and requests</Typography>
              </Box>
              <Box component="li" sx={{ mb: 1 }}>
                <Typography variant="body1">Process ARC (Architectural Review Committee) requests</Typography>
              </Box>
              <Box component="li" sx={{ mb: 1 }}>
                <Typography variant="body1">Send community announcements and important HOA communications</Typography>
              </Box>
              <Box component="li" sx={{ mb: 1 }}>
                <Typography variant="body1">Maintain accurate homeowner records</Typography>
              </Box>
              <Box component="li" sx={{ mb: 1 }}>
                <Typography variant="body1">Process HOA dues payments</Typography>
              </Box>
              <Box component="li" sx={{ mb: 1 }}>
                <Typography variant="body1">Improve our website and services</Typography>
              </Box>
            </Box>

            <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
              Information Sharing
            </Typography>
            <Typography variant="body1" paragraph>
              We do not sell, trade, or rent your personal information to third parties. We may share information only in the following circumstances:
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 2 }}>
              <Box component="li" sx={{ mb: 1 }}>
                <Typography variant="body1">With HOA board members and committees to fulfill their duties</Typography>
              </Box>
              <Box component="li" sx={{ mb: 1 }}>
                <Typography variant="body1">With service providers who assist in HOA operations (e.g., payment processors)</Typography>
              </Box>
              <Box component="li" sx={{ mb: 1 }}>
                <Typography variant="body1">When required by law or to protect the rights of the HOA</Typography>
              </Box>
            </Box>

            <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
              Data Security
            </Typography>
            <Typography variant="body1" paragraph>
              We implement reasonable security measures to protect your personal information from unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet is 100% secure.
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
              Your Rights
            </Typography>
            <Typography variant="body1" paragraph>
              As a resident, you have the right to:
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 2 }}>
              <Box component="li" sx={{ mb: 1 }}>
                <Typography variant="body1">Access your personal information we have on file</Typography>
              </Box>
              <Box component="li" sx={{ mb: 1 }}>
                <Typography variant="body1">Request corrections to inaccurate information</Typography>
              </Box>
              <Box component="li" sx={{ mb: 1 }}>
                <Typography variant="body1">Update your contact information</Typography>
              </Box>
            </Box>

            <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
              Contact Us
            </Typography>
            <Typography variant="body1" paragraph>
              If you have questions about this Privacy Policy or wish to exercise your rights, please contact us at:
            </Typography>
            <Typography variant="body1">
              Email: <Link href="mailto:board@brynleighestates.com">board@brynleighestates.com</Link>
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
              Changes to This Policy
            </Typography>
            <Typography variant="body1" paragraph>
              We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated revision date.
            </Typography>
          </Paper>
        </Box>
      </Container>
    </>
  );
};

export default PrivacyPolicy;
